import {
  clamp,
  cross,
  dot,
  Fn,
  float,
  floor,
  If,
  instancedArray,
  instanceIndex,
  ivec2,
  length,
  max,
  min,
  mix,
  mx_noise_float,
  normalize,
  smoothstep,
  sqrt,
  textureStore,
  uint,
  uniform,
  vec2,
  vec3,
  vec4,
} from 'three/tsl';
import * as THREE from 'three/webgpu';
import { mulberry32 } from '../math/rng';
import type { BiomeField } from './BiomeField';
import { lipDepthNode, type TerrainMouth } from './TerrainMouth';
import {
  EROSION_ROUNDING,
  EROSION_SLOPE_CAP,
  type NoiseLayer,
  octaveFrequencies,
  octaveTaper,
  type TerrainProfile,
} from './TerrainProfile';
import { SphereShape, type TerrainShape } from './TerrainShape';
import type { BakedNoise } from './TerrainStyle';
import { gnoised, NOISE_PERIOD, stripesd } from './terrainNoise';

/** Tiles are 65×65 texels; mesh vertices (33×33) sit on the even texels. */
export const TILE_RES = 65;
export const TILE_TEXELS = TILE_RES * TILE_RES;
/** Rows of a job's description before its octave offsets. */
const HEADER = 2;
/**
 * Ridged noise folds noise with a smooth |n|: sqrt(n² + ε²), rescaled to [0, 1]. A true |n|
 * made crests creases, and flipped the gradient there, so the erosion damping of every finer
 * octave jumped across each crest: the height jumped with it, tens of metres in a step, and
 * drew sheer walls. This keeps crests crisp at a distance but rounded up close.
 */
const RIDGE_EPS = 0.03;
const RIDGE_NORM = 1 / (Math.sqrt(1 + RIDGE_EPS * RIDGE_EPS) - RIDGE_EPS);
/** Step for the lip's gradient (m). */
const LIP_STEP = 0.1;
/** Erosion octaves from this one on count as the narrow gullies (the surface atlas's second channel). */
const NARROW_FROM = 2;

export interface TileJob {
  face: number;
  level: number;
  x: number;
  y: number;
  slot: number;
}

type N = any;

/**
 * Generates terrain tiles on the GPU into an RGBA32F atlas:
 *   r = height (band-limited to this tile's texel size)
 *   g = height band-limited like the parent tile (CDLOD morph target)
 *   b, a = tangential gradient components along the shape's U and V axes
 * Heights are also copied to a storage buffer and read back so the CPU can build
 * collision and answer height queries from exactly the same data.
 * terrainHeight.ts repeats the height recipe on the CPU (for planning a world before it is
 * built): change both together.
 */
export class TileGenerator {
  readonly atlas: THREE.StorageTexture;
  /**
   * With several biomes: each texel's biome weights (rgba, one per biome; see BiomeField),
   * beside the heights in `atlas` (same slots). Null for a world of one biome.
   */
  readonly biomeAtlas: THREE.StorageTexture | null = null;
  /**
   * With erosion: each texel's landform (rgba8, same slots as tlas): r = where it lies
   * between its gullies and ridges (0 a gully's bottom, 0.5 flat or halfway, 1 a crest), all
   * sizes of them; g = the same for the narrow ones only. Null without erosion.
   */
  readonly surfaceAtlas: THREE.StorageTexture | null = null;
  /**
   * The palette's broad noise terms (TerrainStyle.bakedNoise), four to an rgba8 texture, each
   * value v stored as v / 2 + 1/2. Half resolution: a tile's every other texel (BAKED_RES² of
   * them, slot i at i × BAKED_RES instead of i × TILE_RES), as the terms are broad. Empty when
   * the style bakes none.
   */
  readonly bakedAtlases: THREE.StorageTexture[] = [];
  private readonly bakedSize: number;
  readonly slotsPerRow: number;
  readonly maxSlots: number;
  readonly octaves: number[];
  private readonly stride: number;
  private readonly desc: N;
  private readonly heights: N;
  private readonly batchCount = uniform(0, 'uint');
  private readonly compute: N;
  private readonly seedOffset: THREE.Vector3;
  private readonly atlasSize: number;
  private compiled: Promise<void> | null = null;

  constructor(
    readonly renderer: THREE.WebGPURenderer,
    private readonly profile: TerrainProfile,
    readonly shape: TerrainShape,
    seed: number,
    atlasSize: number,
    readonly maxBatch = 24,
    /** Cave mouths: the ground settles into a flat plateau around each (spheres only). */
    readonly mouths: TerrainMouth[] = [],
    /** How the ground is shared out between biomes (spheres only; none: one biome). */
    readonly biomes: BiomeField | null = null,
    /** The palette's broad noise terms, for the tiles to carry (TerrainStyle.bakedNoise). */
    readonly baked: BakedNoise[] = [],
  ) {
    if (baked.length > 8) throw new Error('at most 8 baked noise terms');
    if (mouths.length > 0 && shape.kind !== 'sphere') throw new Error('cave mouths need a spherical hollow');
    if (biomes && shape.kind !== 'sphere') throw new Error('biome fields need a spherical hollow');
    this.atlas = new THREE.StorageTexture(atlasSize, atlasSize);
    this.atlas.type = THREE.FloatType;
    this.atlas.format = THREE.RGBAFormat;
    this.atlas.magFilter = THREE.NearestFilter;
    this.atlas.minFilter = THREE.NearestFilter;
    this.atlas.generateMipmaps = false;
    if (biomes && biomes.count > 1) {
      const b = new THREE.StorageTexture(atlasSize, atlasSize);
      b.type = THREE.UnsignedByteType;
      b.format = THREE.RGBAFormat;
      b.magFilter = THREE.NearestFilter;
      b.minFilter = THREE.NearestFilter;
      b.generateMipmaps = false;
      this.biomeAtlas = b;
    }
    if (profile.erosion) {
      const s = new THREE.StorageTexture(atlasSize, atlasSize);
      s.type = THREE.UnsignedByteType;
      s.format = THREE.RGBAFormat;
      s.magFilter = THREE.NearestFilter;
      s.minFilter = THREE.NearestFilter;
      s.generateMipmaps = false;
      this.surfaceAtlas = s;
    }
    this.bakedSize = Math.floor(atlasSize / TILE_RES) * BAKED_RES;
    for (let i = 0; i < baked.length; i += 4) {
      const t = new THREE.StorageTexture(this.bakedSize, this.bakedSize);
      t.type = THREE.UnsignedByteType;
      t.format = THREE.RGBAFormat;
      t.magFilter = THREE.NearestFilter;
      t.minFilter = THREE.NearestFilter;
      t.generateMipmaps = false;
      this.bakedAtlases.push(t);
    }
    this.atlasSize = atlasSize;
    this.slotsPerRow = Math.floor(atlasSize / TILE_RES);
    this.maxSlots = this.slotsPerRow * this.slotsPerRow;
    this.octaves = octaveFrequencies(profile);
    this.stride = HEADER + this.octaves.length;
    this.desc = instancedArray(maxBatch * this.stride, 'vec4');
    this.heights = instancedArray(maxBatch * TILE_TEXELS, 'float');
    // A large per-hollow shift decorrelates hollows that share a profile.
    const rnd = mulberry32(seed);
    this.seedOffset = new THREE.Vector3(rnd() * 1e6, rnd() * 1e6, rnd() * 1e6);
    this.compute = this.buildCompute().compute(maxBatch * TILE_TEXELS);
  }

  /**
   * Compile the compute shader in the background (once). Await it before the first
   * generate() of a hollow loaded after boot: the first dispatch would otherwise compile
   * it synchronously, stalling a frame.
   */
  precompile(): Promise<void> {
    this.compiled ??= this.renderer.compileComputeAsync(this.compute).then(() => {});
    return this.compiled;
  }

  /**
   * Give the atlas's GPU memory back (the tiles in it are gone). The texture, and every
   * shader that reads it, stays: restoreAtlas() makes it full size again, and three
   * reallocates it the next time it's bound. No recompile.
   */
  releaseAtlas(): void {
    this.atlas.setSize(1, 1, 1);
    this.biomeAtlas?.setSize(1, 1, 1);
    this.surfaceAtlas?.setSize(1, 1, 1);
    for (const t of this.bakedAtlases) t.setSize(1, 1, 1);
  }

  restoreAtlas(): void {
    this.atlas.setSize(this.atlasSize, this.atlasSize, 1);
    this.biomeAtlas?.setSize(this.atlasSize, this.atlasSize, 1);
    this.surfaceAtlas?.setSize(this.atlasSize, this.atlasSize, 1);
    for (const t of this.bakedAtlases) t.setSize(this.bakedSize, this.bakedSize, 1);
  }

  slotOrigin(slot: number): [number, number] {
    return [this.slotX(slot), this.slotY(slot)];
  }

  /** slotOrigin's texel column and row, apart (per frame, without an array each time). */
  slotX(slot: number): number {
    return (slot % this.slotsPerRow) * TILE_RES;
  }

  slotY(slot: number): number {
    return ((slot / this.slotsPerRow) | 0) * TILE_RES;
  }

  /** Approximate texel spacing in metres for a level. */
  texelMeters(level: number): number {
    return this.shape.rootArc / 2 ** level / (TILE_RES - 1);
  }

  /** Dispatch up to maxBatch jobs; resolves with each job's heights (zf). */
  async generate(jobs: TileJob[]): Promise<Float32Array[]> {
    const n = Math.min(jobs.length, this.maxBatch);
    if (n === 0) return [];
    const arr = this.desc.value.array as Float32Array;
    const pc = new THREE.Vector3();
    for (let j = 0; j < n; j++) {
      const job = jobs[j];
      const size = 2 / 2 ** job.level;
      const uc = -1 + (job.x + 0.5) * size;
      const vc = -1 + (job.y + 0.5) * size;
      const [sx, sy] = this.slotOrigin(job.slot);
      let o = j * this.stride * 4;
      arr.set([job.face, uc, vc, size], o);
      o += 4;
      arr.set([sx, sy, 0, this.texelMeters(job.level)], o);
      o += 4;
      // Exact lattice offsets of the tile centre for every octave, reduced mod the
      // noise period in f64. The GPU only adds small tile-local offsets to these.
      this.shape.point(job.face, uc, vc, pc).add(this.seedOffset);
      for (const f of this.octaves) {
        arr[o] = posMod(pc.x * f, NOISE_PERIOD);
        arr[o + 1] = posMod(pc.y * f, NOISE_PERIOD);
        arr[o + 2] = posMod(pc.z * f, NOISE_PERIOD);
        arr[o + 3] = 0;
        o += 4;
      }
    }
    this.desc.value.needsUpdate = true;
    this.batchCount.value = n;
    this.renderer.compute(this.compute);
    const buf = await this.renderer.getArrayBufferAsync(this.heights.value, null, 0, n * TILE_TEXELS * 4);
    const all = new Float32Array(buf);
    const out: Float32Array[] = [];
    for (let j = 0; j < n; j++) out.push(all.slice(j * TILE_TEXELS, (j + 1) * TILE_TEXELS));
    return out;
  }

  private buildCompute() {
    const p = this.profile;
    // Relief (see below) compares the land with its shape at this scale and wider: about a
    // twelfth of the way round a cube face (1.5 km on Verdant), a few hundred metres at least.
    const lowWave = Math.max(250, 0.08 * this.shape.rootArc);
    // The height difference that counts as fully raised or sunk (m).
    const reliefScale = 0.12 * p.ridgeHeight + 0.6 * p.hillHeight + 5;
    const stride = this.stride;
    const desc = this.desc;
    const heights = this.heights;
    const atlas = this.atlas;
    const biomeAtlas = this.biomeAtlas;
    const surfaceAtlas = this.surfaceAtlas;
    const bakedAtlases = this.bakedAtlases;
    const baked = this.baked;
    const batchCount = this.batchCount;
    const freqs = this.octaves;

    return Fn(() => {
      const idx = instanceIndex;
      const tile = idx.div(uint(TILE_TEXELS));
      If(tile.lessThan(batchCount), () => {
        const texel = idx.sub(tile.mul(uint(TILE_TEXELS)));
        const ti = texel.mod(uint(TILE_RES));
        const tj = texel.div(uint(TILE_RES));
        const base = tile.mul(uint(stride));
        const d0 = desc.element(base);
        const d1 = desc.element(base.add(uint(1)));
        const texelM = d1.w;

        // Where this texel lies, relative to the tile centre (whose exact position the
        // octave offsets already hold), and which way the surface faces.
        const g = vec2(float(ti), float(tj)).div(TILE_RES - 1);
        const surf = this.shape.gpu.at(d0.x, d0.y, d0.z, d0.w, g);
        const delta = surf.offset.toVar();
        const dir = surf.normal.toVar();

        let k = 0;
        const octave = (f: number): N => {
          const off = desc.element(base.add(uint(HEADER + k))).xyz;
          k++;
          const n = gnoised(off.add(delta.mul(f)));
          return vec4(n.x, n.yzw.mul(f));
        };
        // Band limiting: fade octaves whose wavelength approaches the texel size.
        const wFine = (lambda: number) => smoothstep(texelM.mul(2.0), texelM.mul(4.0), float(lambda));
        const wCoarse = (lambda: number) => smoothstep(texelM.mul(4.0), texelM.mul(8.0), float(lambda));

        // `local` layers' finer octaves are tapered (see Smoothing); the normalising total isn't.
        // `low`: the sum over just the octaves of wavelength LOW_WAVE or more (see relief below).
        const fbm = (layer: NoiseLayer, local = false) => {
          const sum = vec4(0).toVar();
          const sumC = vec4(0).toVar();
          const low = vec4(0).toVar();
          let amp = 1;
          let f = 1 / layer.wavelength;
          let total = 0;
          for (let i = 0; i < layer.octaves; i++) {
            const n = octave(f);
            const a = amp * (local ? octaveTaper(p, 1 / f) : 1);
            sum.addAssign(n.mul(wFine(1 / f).mul(a)));
            sumC.addAssign(n.mul(wCoarse(1 / f).mul(a)));
            if (1 / f >= lowWave) low.assign(sum);
            total += amp;
            amp *= layer.gain;
            f *= layer.lacunarity;
          }
          return { fine: sum.div(total), coarse: sumC.div(total), low: low.div(total) };
        };
        const ridged = (layer: NoiseLayer, damping: number) => {
          // Fine and coarse bands each track their own gradient for the erosion-like
          // damping, so a tile's coarse band equals its parent's fine band exactly. (The octaves
          // run from the widest, so `low` is the fine sum as it stood after the last wide one.)
          const sum = vec4(0).toVar();
          const sumC = vec4(0).toVar();
          const low = vec4(0).toVar();
          let amp = 1;
          let f = 1 / layer.wavelength;
          let total = 0;
          for (let i = 0; i < layer.octaves; i++) {
            const n = octave(f);
            const a = amp * octaveTaper(p, 1 / f);
            const sa = sqrt(n.x.mul(n.x).add(RIDGE_EPS * RIDGE_EPS));
            const r = float(1).sub(sa.sub(RIDGE_EPS).mul(RIDGE_NORM));
            const dr = n.yzw.mul(n.x.div(sa).mul(-RIDGE_NORM));
            const r2 = vec4(r.mul(r), dr.mul(r.mul(2)));
            const gs = sum.yzw.mul(layer.wavelength);
            const gc = sumC.yzw.mul(layer.wavelength);
            const damp = float(1).div(float(1).add(dot(gs, gs).mul(damping * 0.02)));
            const dampC = float(1).div(float(1).add(dot(gc, gc).mul(damping * 0.02)));
            sum.addAssign(
              r2.mul(
                wFine(1 / f)
                  .mul(damp)
                  .mul(a),
              ),
            );
            sumC.addAssign(
              r2.mul(
                wCoarse(1 / f)
                  .mul(dampC)
                  .mul(a),
              ),
            );
            if (1 / f >= lowWave) low.assign(sum);
            total += amp;
            amp *= layer.gain;
            f *= layer.lacunarity;
          }
          return { fine: sum.div(total), coarse: sumC.div(total), low: low.div(total) };
        };
        const smoothD = (e0: number, e1: number, a: N) => {
          const t = clamp(a.x.sub(e0).div(e1 - e0), 0, 1);
          const v = t.mul(t).mul(float(3).sub(t.mul(2)));
          const dv = t.mul(float(1).sub(t)).mul(6 / (e1 - e0));
          return vec4(v, a.yzw.mul(dv));
        };
        const cont = fbm(p.continents);
        const mask = fbm(p.mountainMask);
        const ridge = ridged(p.ridges, p.ridgeDamping);
        const hills = fbm(p.hills, true);
        const detail = fbm(p.detail, true);

        /** The land's shape in one band (height and gradient), before erosion and the fine detail. */
        const landShape = (c: N, mk: N, rg: N, hl: N): N => {
          // Mountains only rise on land and inside the mask.
          const landness = smoothD(-0.05, 0.25, vec4(c.x.add(p.landBias), c.yzw));
          const m = smoothD(-0.1, 0.35, mk);
          const mm: N = vec4(m.x.mul(landness.x), m.yzw.mul(landness.x).add(landness.yzw.mul(m.x)));
          const mountains = vec4(mm.x.mul(rg.x), mm.yzw.mul(rg.x).add(rg.yzw.mul(mm.x))).mul(p.ridgeHeight);
          return vec4(c.x.add(p.landBias), c.yzw)
            .mul(p.continentHeight)
            .add(mountains)
            .add(hl.mul(p.hillHeight));
        };
        // As variables, evaluated here: a node first used inside a branch (the erosion's below)
        // is computed only in that branch, and reads as 0 after it wherever the branch is skipped.
        let shapeF: N = landShape(cont.fine, mask.fine, ridge.fine, hills.fine).toVar();
        // Relief: how far the land stands above (or sinks below) its shape at the scale of whole
        // valleys and ranges, in −1 … 1. Palettes shade hollows and spurs by it.
        const relief = clamp(
          shapeF.x.sub(landShape(cont.low, mask.low, ridge.low, hills.low).x).div(reliefScale),
          -1,
          1,
        ).toVar();
        let shapeC: N = landShape(cont.coarse, mask.coarse, ridge.coarse, hills.coarse).toVar();

        // Erosion (terrainHeight.ts does the same on the CPU): gullies cut across the slope,
        // each octave following the ground as eroded so far. The coarse band runs it again
        // with its own band limits, so it still equals the parent tile's fine band.
        let ridges: N = null;
        let reliefF: N = relief;
        const e = p.erosion;
        if (e) {
          const first = k;
          const erode = (shape: N, weight: (lambda: number) => N) => {
            const tangent = (v: N): N => v.sub((dir as N).mul(dot(v, dir)));
            const slope0 = length(tangent(shape.yzw)).toVar();
            const cut = min(slope0, EROSION_SLOPE_CAP).mul(e.strength).toVar();
            const acc = vec4(0).toVar();
            const all = float(0).toVar();
            const narrow = float(0).toVar();
            let lambda = e.wavelength;
            let amp = 1;
            let total = 0;
            let totalNarrow = 0;
            for (let i = 0; i < e.octaves; i++) {
              const wt = weight(lambda);
              const f = 1 / lambda;
              const a = (lambda * amp) / (2 * Math.PI);
              const ampNow = amp;
              const off = desc.element(base.add(uint(HEADER + first + i))).xyz;
              If(wt.greaterThan(0), () => {
                const across: N = cross(dir as N, tangent(shape.yzw.add(acc.yzw)));
                const w = across.div(max(length(across), 1e-12));
                const n = stripesd(off.add(delta.mul(f)), w);
                // V-shaped gullies, rounded crests (see EROSION_ROUNDING).
                const shaped = n.x.sub(n.x.mul(n.x).mul(EROSION_ROUNDING));
                const slopeK = float(1).sub(n.x.mul(2 * EROSION_ROUNDING));
                acc.addAssign(vec4(shaped, n.yzw.mul(slopeK.mul(f))).mul(cut.mul(wt).mul(a)));
                const r = n.x.mul(wt).mul(ampNow);
                all.addAssign(r);
                if (i >= NARROW_FROM) narrow.addAssign(r);
              });
              total += amp;
              if (i >= NARROW_FROM) totalNarrow += amp;
              lambda /= e.lacunarity;
              amp *= e.gain;
            }
            const presence = smoothstep(0.02, 0.15, slope0);
            return {
              h: acc,
              ridge: all.mul(presence).div(total),
              narrow: totalNarrow > 0 ? narrow.mul(presence).div(totalNarrow) : float(0),
            };
          };
          const fine = erode(shapeF, wFine);
          const coarse = erode(shapeC, wCoarse);
          shapeF = shapeF.add(fine.h);
          shapeC = shapeC.add(coarse.h);
          ridges = vec2(fine.ridge, fine.narrow);
          k += e.octaves;
        }
        if (k !== freqs.length) throw new Error('octave table mismatch');
        const h = shapeF.add(detail.fine.mul(p.detailHeight));
        const hc = shapeC.x.add(detail.coarse.x.mul(p.detailHeight));
        // Cave mouths: settle the ground into a flat plateau (value, gradient and the
        // coarse morph band alike), blending back into the natural terrain around it.
        let hF: N = h;
        let hcF: N = hc;
        const R = this.shape instanceof SphereShape ? this.shape.radius : 0;
        for (const m of this.mouths) {
          const chord = dir.sub(vec3(m.dir.x, m.dir.y, m.dir.z));
          const cl = max(length(chord), 1e-9);
          const t = clamp(cl.mul(R).sub(m.flat).div(m.blend), 0, 1);
          const w = float(1).sub(t.mul(t).mul(float(3).sub(t.mul(2))));
          const dw = t.mul(float(1).sub(t)).mul(-6 / m.blend); // dw/ds; ∂s/∂x = chord / |chord|
          const hv = hF.x;
          const grad = hF.yzw.mul(float(1).sub(w)).add(chord.div(cl).mul(dw.mul(float(m.height).sub(hv))));
          hF = vec4(mix(hv, float(m.height), w), grad);
          hcF = mix(hcF, float(m.height), w);
          if (ridges) ridges = ridges.mul(float(1).sub(w));
          reliefF = reliefF.mul(float(1).sub(w));

          // The lip: near the hole the plateau rolls over into it (lipDepthAt). Its gradient
          // by finite differences along an orthonormal tangent basis (LIP_STEP metres): a
          // cube face's U and V axes are only tangent at the face's centre.
          const Rp = R - m.height;
          const e1 = normalize(surf.U.sub(dir.mul(dot(surf.U, dir))));
          const e2 = cross(dir as N, e1 as N);
          const dip = lipDepthNode(m, Rp, dir);
          const dip1 = lipDepthNode(m, Rp, normalize(dir.mul(Rp).add(e1.mul(LIP_STEP))));
          const dip2 = lipDepthNode(m, Rp, normalize(dir.mul(Rp).add(e2.mul(LIP_STEP))));
          const gradDip = e1
            .mul(dip1.sub(dip))
            .add(e2.mul(dip2.sub(dip)))
            .div(LIP_STEP);
          hF = vec4(hF.x.sub(dip), hF.yzw.sub(gradDip));
          hcF = hcF.sub(dip);
        }

        // Tangential gradient, stored along the shape's U and V axes.
        const grad = hF.yzw;
        const gt = grad.sub(dir.mul(dot(grad, dir)));
        const texCoord = ivec2(d1.x.add(float(ti)), d1.y.add(float(tj)));
        textureStore(atlas, texCoord, vec4(hF.x, hcF, dot(gt, surf.U), dot(gt, surf.V)));
        if (biomeAtlas && this.biomes) textureStore(biomeAtlas, texCoord, this.biomes.node(dir, hF.x));
        if (surfaceAtlas && ridges)
          textureStore(surfaceAtlas, texCoord, vec4(ridges.mul(0.5).add(0.5), reliefF.mul(0.5).add(0.5), 1));
        if (bakedAtlases.length > 0) {
          // The palette's broad terms, at the ground point the terrain shader calls p (vHollow),
          // on every other texel (the baked atlases are half resolution).
          If(
            ti
              .mod(uint(2))
              .equal(uint(0))
              .and(tj.mod(uint(2)).equal(uint(0))),
            () => {
              const ground: N = (surf.local as N).sub((dir as N).mul(max(hF.x, 0)));
              const term = (b: BakedNoise | undefined): N => {
                if (!b) return float(0);
                let x: N = ground.mul(1 / b.wavelength);
                if (b.offset) x = x.add(vec3(...b.offset));
                return mx_noise_float(x).mul(0.5).add(0.5);
              };
              const origin: N = floor(d1.xy.div(TILE_RES).add(0.5)).mul(BAKED_RES);
              const halfCoord = ivec2(
                origin.x.add(float((ti as N).div(uint(2)))),
                origin.y.add(float((tj as N).div(uint(2)))),
              );
              bakedAtlases.forEach((t, i) => {
                const k = i * 4;
                textureStore(
                  t,
                  halfCoord,
                  vec4(term(baked[k]), term(baked[k + 1]), term(baked[k + 2]), term(baked[k + 3])),
                );
              });
            },
          );
        }
        heights.element(tile.mul(uint(TILE_TEXELS)).add(texel)).assign(hF.x);
      });
    })();
  }
}

/** Texels per wavelength a baked noise term needs (fewer, and interpolating them shows). */
const BAKE_TEXELS = 2.5;
/** A tile's texels per side in the baked atlases (every other one of its TILE_RES). */
export const BAKED_RES = (TILE_RES + 1) / 2;

/**
 * The terms of `terms` the tiles can carry in a world whose roots span `rootArc` metres, seen
 * from up to `maxDistance` metres away with this split factor (TerrainQuadtree.splitFactor):
 * those with BAKE_TEXELS texels per wavelength in the coarsest tile it draws (at full detail:
 * when a full atlas lowers it, the farthest tiles hold them a little coarser). Verdant keeps all of
 * its terms; Titan, 2,000 km across, none.
 */
export function bakeable(
  terms: BakedNoise[],
  rootArc: number,
  splitFactor: number,
  maxDistance: number,
): BakedNoise[] {
  const level = Math.max(0, Math.ceil(Math.log2((splitFactor * rootArc) / maxDistance)));
  const texel = rootArc / 2 ** level / (BAKED_RES - 1);
  return terms.filter((t) => t.wavelength >= BAKE_TEXELS * texel);
}

function posMod(x: number, m: number): number {
  const r = x % m;
  return r < 0 ? r + m : r;
}
